import { isReservationSoon, requestMapFit, useAppStore, useTodayReservations } from "@/store/store";
import { useShallow } from "zustand/shallow";
import { CalendarDays, ChevronLeft, ChevronRight, Hourglass, Plus } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { dayjs, parseTime, todayStr } from "@/utils/date";
import type { Reservation } from "@/store/types";
import MapCanvas from "./MapCanvas";
import MapCanvasView from "./MapCanvasView";
import Sidebar from "./Sidebar";

// ── Collapsed mini-sidebar ──────────────────────────────────────

function CollapsedSidebar({ onExpand }: { onExpand: () => void }) {
  const { selectedDate, tables, reservations } = useAppStore(
    useShallow((s) => ({
      selectedDate: s.selectedDate,
      tables: s.tables,
      reservations: s.reservations,
    })),
  );

  const todayRes = reservations.filter(
    (r) => r.date === selectedDate && !["cancelada", "completada"].includes(r.status),
  );

  const groups: Record<string, Reservation[]> = {};
  for (const r of todayRes) {
    (groups[r.time] ??= []).push(r);
  }
  const sortedEntries = Object.entries(groups).toSorted(
    ([a], [b]) => parseTime(a).valueOf() - parseTime(b).valueOf(),
  );

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const getTableLabel = (r: Reservation) =>
    r.tableIds.length > 0
      ? r.tableIds.map((id) => tables.find((t) => t.id === id)?.name || "?").join("+")
      : "—";

  const getAvatarStyle = (r: Reservation) => {
    if (r.status === "sentado") return "bg-status-seated text-white";
    if (r.status === "noshow") return "bg-red-100 text-red-700";
    if (r.status === "reservada" && isReservationSoon(r)) return "bg-yellow-100 text-yellow-800";
    return "bg-dark text-white";
  };

  const isToday = selectedDate === todayStr();

  return (
    <div className="flex h-full w-full flex-col bg-surface rounded-2xl overflow-hidden">
      {/* Header — date + expand */}
      <div className="flex flex-col items-center gap-1 px-2 pt-3 pb-4 border-b border-border">
        <button
          type="button"
          onClick={onExpand}
          className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:bg-accent"
          aria-label="Expandir sidebar"
        >
          <ChevronLeft className="h-4 w-4 text-text-secondary" />
        </button>
        <span className="text-[11px] font-bold text-text-primary">
          {dayjs(selectedDate).format("D")}
        </span>
        <span className="text-[9px] uppercase text-text-secondary leading-none">
          {dayjs(selectedDate).format("MMM")}
        </span>
        {isToday && <span className="text-[8px] font-medium text-primary">Hoy</span>}
      </div>

      {/* Reservation items */}
      <div className="flex-1 overflow-y-auto px-1.5 py-2 flex flex-col gap-3">
        {sortedEntries.map(([time, resos]) => (
          <div key={time} className="flex flex-col items-center gap-1">
            <span className="text-[9px] font-semibold text-text-secondary">{time}</span>
            {resos.map((r) => (
              <Tooltip key={r.id}>
                <TooltipTrigger
                  className="flex flex-col items-center gap-0.5 w-full cursor-pointer"
                  onClick={onExpand}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold ${getAvatarStyle(r)}`}
                  >
                    {getInitials(r.guestName)}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p className="font-medium">{r.guestName}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.pax}p · {getTableLabel(r)} · {time}
                  </p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        ))}
        {sortedEntries.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-[10px] text-text-muted text-center">Sin reservas</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main layout ─────────────────────────────────────────────────

export default function DashboardLayout() {
  const {
    mapMode,
    showSidebar,
    setShowSidebar,
    openNewReservation,
    openNewWaitlist,
    sidebarCollapsed,
    setSidebarCollapsed,
    isEditingReservation,
    detailReservationId,
    activeTab,
  } = useAppStore(
    useShallow((s) => ({
      mapMode: s.mapMode,
      openNewReservation: s.openNewReservation,
      openNewWaitlist: s.openNewWaitlist,
      setShowSidebar: s.setShowSidebar,
      setSidebarCollapsed: s.setSidebarCollapsed,
      showSidebar: s.showSidebar,
      sidebarCollapsed: s.sidebarCollapsed,
      isEditingReservation: s.isEditingReservation,
      detailReservationId: s.detailReservationId,
      activeTab: s.activeTab,
    })),
  );
  // Ocultar el CTA "Nueva reserva" del pie del sidebar cuando el
  // operador está dentro de una reserva (detalle abierto) o editándola
  // — el foco está en esa reserva, no en crear otra.
  const hideCreateCta = isEditingReservation || !!detailReservationId;
  const todayRes = useTodayReservations();
  const activeCount = todayRes.filter(
    (r) => !["cancelada", "completada"].includes(r.status),
  ).length;

  const isEdit = mapMode === "edit";

  return (
    <div className="h-full min-h-0 overflow-hidden relative flex flex-col">
      <div className="flex-1 min-h-0 min-w-0 flex flex-col">
        {isEdit ? <MapCanvas /> : <MapCanvasView />}
      </div>

      {!isEdit && (
        <>
          {/* Desktop sidebar — smooth collapse/expand */}
          <div
            className="hidden lg:flex fixed z-30"
            style={{
              bottom: "calc(1.25rem + var(--safe-bottom))",
              right: "calc(1.25rem + var(--safe-right))",
              top: "calc(1.25rem + var(--safe-top))",
              width: sidebarCollapsed ? "64px" : "min(460px, 36vw)",
              transition: "width 400ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div
              className="relative flex flex-1 flex-col rounded-2xl bg-white/95 backdrop-blur-sm overflow-hidden"
              style={{ boxShadow: "0 4px 30px rgba(0,0,0,0.1)" }}
            >
              {/* Collapse handle — left edge */}
              <button
                type="button"
                onClick={() => {
                  setSidebarCollapsed(true);
                  setTimeout(requestMapFit, 450);
                }}
                className="absolute left-0 top-1/2 z-10 flex h-12 w-5 -translate-y-1/2 items-center justify-center rounded-r-lg bg-white/80 border border-l-0 border-border text-text-secondary transition-opacity hover:bg-accent"
                style={{
                  opacity: sidebarCollapsed ? 0 : 1,
                  pointerEvents: sidebarCollapsed ? "none" : "auto",
                  transition: "opacity 250ms ease",
                }}
                aria-label="Colapsar sidebar"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>

              {/* Content area — flex-1 with relative for absolute layers */}
              <div className="relative flex-1 min-h-0">
                {/* Expanded sidebar — fades out when collapsing */}
                <div
                  className="absolute inset-0 flex flex-col"
                  style={{
                    opacity: sidebarCollapsed ? 0 : 1,
                    pointerEvents: sidebarCollapsed ? "none" : "auto",
                    transition: "opacity 250ms ease",
                  }}
                >
                  <Sidebar />
                </div>

                {/* Collapsed sidebar — fades in when collapsing */}
                <div
                  className="absolute inset-0"
                  style={{
                    opacity: sidebarCollapsed ? 1 : 0,
                    pointerEvents: sidebarCollapsed ? "auto" : "none",
                    transition: "opacity 250ms ease 150ms",
                  }}
                >
                  <TooltipProvider>
                    <CollapsedSidebar
                      onExpand={() => {
                        setSidebarCollapsed(false);
                        setTimeout(requestMapFit, 450);
                      }}
                    />
                  </TooltipProvider>
                </div>
              </div>

              {/* Create actions — bottom, hidden while editing another reservation
                  so the operator's focus stays on the in-flight edit. Expanded:
                  "Nueva reserva" (black) + "Agregar a lista de espera" (green) side by side.
                  Collapsed (64px rail): the same two as stacked icon buttons. */}
              {!hideCreateCta && (
                <div
                  className={`shrink-0 p-2 ${
                    sidebarCollapsed
                      ? "flex flex-col items-center gap-2"
                      : "flex items-stretch gap-2"
                  }`}
                >
                  <Button
                    onClick={() => openNewReservation()}
                    title="Nueva reserva"
                    className={`shadow-sm ${
                      sidebarCollapsed
                        ? "w-10 h-10 rounded-full p-0"
                        : "h-12 flex-1 gap-2 rounded-xl"
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    {!sidebarCollapsed && (
                      <span className="text-sm font-semibold">Nueva reserva</span>
                    )}
                  </Button>
                  {activeTab === "espera" && (
                    <Button
                      title="Agregar a lista de espera"
                      onClick={() => openNewWaitlist()}
                      className={`bg-green-600 text-white shadow-sm hover:bg-green-700 ${
                        sidebarCollapsed
                          ? "w-10 h-10 rounded-full p-0"
                          : "h-12 flex-1 gap-2 rounded-xl"
                      }`}
                    >
                      <Hourglass className="w-4 h-4" />
                      {!sidebarCollapsed && (
                        <span className="text-sm font-semibold">Agregar a lista de espera</span>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sheet — opened from MapCanvasView (table tap) AND from the
              tablet-portrait floating button below. The bottom nav (<md)
              and the desktop floating sidebar (lg+) cover the other ranges. */}
          <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
            <SheetContent
              side="right"
              className="w-full max-w-105 p-0 pointer-coarse:max-w-[min(100vw,28rem)]"
              showCloseButton={false}
            >
              <Sidebar />
            </SheetContent>
          </Sheet>

          {/* Tablet portrait gap (md–<lg): bottom nav is hidden (md:hidden)
              and the desktop floating sidebar is hidden (lg:flex), so the
              host needs floating affordances here to start a new reservation
              and to open the listado overlay without leaving the map. Both
              clustered bottom-right so they don't crowd the left rail. The
              "Nueva reserva" button is hidden while another reservation is
              being edited so a stray click doesn't steal focus from the
              in-flight edit. */}
          <div
            className="hidden md:flex lg:hidden fixed z-50 flex-col items-end gap-3"
            style={{
              bottom: "calc(1rem + var(--safe-bottom))",
              right: "calc(1rem + var(--safe-right))",
            }}
          >
            <Button
              onClick={() => setShowSidebar(true)}
              className="rounded-full shadow-lg gap-2 px-5 h-12"
            >
              <CalendarDays className="w-5 h-5" />
              <span className="text-sm font-semibold">Reservas</span>
              {activeCount > 0 && (
                <span className="bg-white text-primary text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {activeCount}
                </span>
              )}
            </Button>
            {!hideCreateCta && (
              <Button
                onClick={() => {
                  // Tablet portrait: the desktop floating sidebar is
                  // hidden here, so the Nueva reserva surface needs
                  // the Sheet to be visible. Open both.
                  setShowSidebar(true);
                  openNewReservation();
                }}
                title="Nueva reserva"
                size="icon-lg"
                className="w-14 h-14 rounded-full shadow-lg self-end"
              >
                <Plus className="w-5 h-5" />
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
